import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Alert,
  Anchor,
  Stack,
  Center
} from '@mantine/core';
import { useAuth } from '../contexts/AuthContext';

// Reached via the activation link emailed after an admin approves a beta
// signup request (see backend/routes/admin.js's approve-beta) — sets a
// password for the first time on an already-approved, previously
// passwordless account. Replaces the old open RegisterPage.js, which used
// to create a brand-new account directly; there is no other way to create
// an account now besides the beta-request form (see BetaSignupModal.js).
const ActivatePage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const code = searchParams.get('code') || '';

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { activate } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 12) {
      setError('Password must be at least 12 characters long');
      return;
    }

    setLoading(true);

    const result = await activate(email, code, formData.password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  if (!email || !code) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Container size={420} my={40}>
          <Paper withBorder shadow="md" p={30} radius="md">
            <Alert color="red" title="Invalid activation link">
              This link is missing required information. Please use the link from your approval email.
            </Alert>
            <Text c="dimmed" size="sm" ta="center" mt={20}>
              <Anchor component={Link} to="/login" size="sm">
                Back to sign in
              </Anchor>
            </Text>
          </Paper>
        </Container>
      </Center>
    );
  }

  return (
    <Center style={{ minHeight: '100vh' }}>
      <Container size={420} my={40}>
        <Paper withBorder shadow="md" p={30} radius="md">
          <Title order={2} ta="center" mb={5}>
            Set Your Password
          </Title>
          <Text c="dimmed" size="sm" ta="center" mb={20}>
            Your beta access for {email} was approved — set a password to finish creating your account
          </Text>

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              {error && (
                <Alert color="red" title="Error">
                  {error}
                </Alert>
              )}

              <TextInput
                label="Email"
                value={email}
                disabled
                size="md"
              />

              <PasswordInput
                label="Password"
                placeholder="Create a password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                size="md"
              />

              <PasswordInput
                label="Confirm Password"
                placeholder="Confirm your password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                size="md"
              />

              <Button
                type="submit"
                fullWidth
                size="md"
                loading={loading}
              >
                Set Password &amp; Sign In
              </Button>
            </Stack>
          </form>

          <Text c="dimmed" size="sm" ta="center" mt={20}>
            <Anchor component={Link} to="/login" size="sm">
              Back to sign in
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Center>
  );
};

export default ActivatePage;
